import { formatDate } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { MdOutlineCalendarToday } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IListDailyPostsParams, TCatalogPostOriginType } from '~/types/catalog'

import deleteIcon from '~/assets/daily-post-delete-icon.png'
import ActionMenu from '~/components/ActionMenu'
import ModalDialog from '~/components/ModalDialog'
import Tooltip from '~/components/Tooltip'
import { AUTHORIZATIONS, TOAST_ERRORS, TODAY } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { keys as catalogKeys, useDeleteDailyPostMutation } from '~/queries/catalog'
import { useFileDetailsQuery } from '~/queries/files'
import dayjs from '~/utils/dayjs'
import { handleGenericError } from '~/utils/error'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  cleanedPrompt?: string
  coloringTemplateId?: string
  day: string
  eventTooltipPlacement?: 'top' | 'topRight' | 'topLeft'
  events?: string[]
  listDailyPostParams: IListDailyPostsParams
  onOpenAddToCategoryModal: (id: string) => void
  onOpenAddToDailyModal: (id: string) => void
  onOpenPreview: (mediaUrl: string) => void
  originType?: TCatalogPostOriginType
  previewFileId?: string
}

export const DailyPost = ({
  cleanedPrompt,
  coloringTemplateId,
  day,
  events,
  eventTooltipPlacement = 'top',
  listDailyPostParams,
  onOpenAddToCategoryModal,
  onOpenAddToDailyModal,
  onOpenPreview,
  originType,
  previewFileId,
}: Props): ReactElement => {
  /** Local state  */

  const isUserWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.CCC.CATALOG.WRITE])

  const queryClient = useQueryClient()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const componentClasses = classNames('daily-post-component', {
    'daily-post-component--done': dayjs(day).isBefore(dayjs(TODAY).startOf('day')),
    'daily-post-component--event': events && events.length > 0,
    'daily-post-component--ml': originType === 'ml',
  })

  /** Queries & Mutations */

  const { data: fileDetailsData } = useFileDetailsQuery(previewFileId)

  const { mutateAsync: deleteDailyPostMutation } = useDeleteDailyPostMutation()

  /** Handlers */

  const handleDeleteModalState = (value: boolean) => () => setIsDeleteModalOpen(value)

  const handleDeleteDailyPostConfirm = async () => {
    if (!isUserWriteAuthorized) {
      displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
      return
    }

    setIsLoading(true)

    try {
      await deleteDailyPostMutation(day)
      await queryClient.invalidateQueries({
        queryKey: catalogKeys.dailyPosts({
          endDate: listDailyPostParams.endDate,
          startDate: listDailyPostParams.startDate,
        }),
      })
      setIsDeleteModalOpen(false)
      displayToast('Post was successfully deleted', 'success')
    } catch (e) {
      handleGenericError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyId = useCallback(() => copyText(previewFileId), [previewFileId])

  /** Render */

  const menu: Menu | undefined = useMemo(
    () =>
      coloringTemplateId
        ? {
            actions: [
              {
                action: () => onOpenAddToCategoryModal(coloringTemplateId),
                label: 'Add to category',
              },
              {
                action: () => onOpenAddToDailyModal(coloringTemplateId),
                label: 'Assign to a day',
              },
              {
                action: () => handleCopyId(),
                label: 'Copy the ID',
              },
            ],
          }
        : undefined,
    [handleCopyId, onOpenAddToCategoryModal, onOpenAddToDailyModal, coloringTemplateId]
  )

  return (
    <>
      <div className={componentClasses}>
        <div className="daily-post-component__info">
          <div
            className={classNames('daily-post-component__info__date', {
              'daily-post-component__info__date--event': events && events?.length > 0,
            })}
            title={formatDate(day, 'LL')}
          >
            <span className="daily-post-component__info__date__text">{formatDate(day, 'D')}</span>
            {events && events?.length > 0 ? (
              <Tooltip
                className="daily-post-component__info__date__event-tooltip"
                overlay={
                  events.length > 1 ? (
                    <ul>
                      {events.map((el, idx) => (
                        <li key={idx}>{el}</li>
                      ))}
                    </ul>
                  ) : (
                    <span>{events[0]}</span>
                  )
                }
                placement={eventTooltipPlacement}
                trigger="hover"
              >
                <div className="daily-post-component__info__date__event-icon-wrapper">
                  <MdOutlineCalendarToday color="#fff" size={16} />
                </div>
              </Tooltip>
            ) : null}
          </div>
          {cleanedPrompt ? (
            <span className="daily-post-component__info__ml-prompt" title={cleanedPrompt}>
              {cleanedPrompt}
            </span>
          ) : null}
        </div>
        {menu ? <ActionMenu className="daily-post-component__actions-menu" menu={menu} /> : null}
        <div className="drawing-and-button">
          {previewFileId ? (
            <button className="drawing-and-button__delete-button" onClick={handleDeleteModalState(true)}>
              <img src={deleteIcon} />
            </button>
          ) : null}
          <div
            className={classNames('drawing-and-button__drawing-wrapper', {
              'drawing-and-button__drawing-wrapper--empty': !fileDetailsData?.download_url,
            })}
          >
            {fileDetailsData?.download_url ? (
              <img
                className="drawing-and-button__drawing-wrapper__image"
                onClick={() => onOpenPreview(fileDetailsData.download_url)}
                src={fileDetailsData.download_url}
              />
            ) : null}
          </div>
        </div>
      </div>
      <ModalDialog
        isLoading={isLoading}
        isOpen={isDeleteModalOpen}
        mainText={`Delete post assigned to ${formatDate(day, 'LL')}?`}
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteDailyPostConfirm}
      />
    </>
  )
}
